import { AxiosBaseQueryFunction } from 'api/axiosBaseQuery';

import { QueryDefinition, skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';

import { useIsLaminateSelected } from '../materials-api/hooks/useIsLaminateSelected';

import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectZipcode } from '~/redux/slices/zipcode';

type LaminateQueryDefinition<T> = QueryDefinition<
  CommonProductQueryParams,
  AxiosBaseQueryFunction,
  string,
  T[],
  'easyEstimatorApi'
>;

type UseLaminateProductQuery<T> = UseQuery<LaminateQueryDefinition<T>>;

export type LaminateProductHookReturn<T> = Omit<
  TypedUseQueryHookResult<
    T[],
    CommonProductQueryParams,
    AxiosBaseQueryFunction
  >,
  'data'
> & {
  data: T[];
};

export const createLaminateProductHook = <T>(
  useQueryHook: UseLaminateProductQuery<T>
): (() => LaminateProductHookReturn<T>) => {
  const emptyArray = [] as T[];

  return () => {
    const zipCode = useAppSelector(selectZipcode);
    const selectedColor = useAppSelector(selectSelectedColor);
    const selectedStore = useSelectedStore();
    const isLaminateSelected = useIsLaminateSelected();

    const shouldFetch =
      isLaminateSelected &&
      zipCode &&
      selectedStore?.companyId &&
      selectedColor?.qteGrpID;

    const { data, ...rest } = useQueryHook(
      shouldFetch
        ? {
            params: {
              zipCode,
              storeId: selectedStore?.companyId,
              prodQteGrpID: selectedColor?.qteGrpID ?? undefined,
            },
          }
        : skipToken,
      {
        selectFromResult: (result) => ({
          data: result.isSuccess && result.data ? result.data : emptyArray,
          ...result,
        }),
      }
    );

    return {
      data: data ?? emptyArray,
      ...rest,
    };
  };
};
